import React, { ReactNode, useEffect, useMemo, useRef, useState } from 'react';
import { AlertCircle, Loader2, MessageSquare, Send, Sparkles, X } from 'lucide-react';
import { ProjectChatMessage } from '../../types';

interface ChatPanelProps {
  messages: ProjectChatMessage[];
  title?: string | null;
  subtitle?: string | null;
  conversationKey?: string | null;
  onSubmit: (prompt: string) => Promise<void> | void;
  loading?: boolean;
  disabled?: boolean;
  error?: string | null;
  onDismissError?: () => void;
  draftValue?: string;
  onDraftChange?: (value: string) => void;
  initialScrollTop?: number | null;
  onScrollPositionChange?: (scrollTop: number) => void;
  emptyStateEyebrow?: string;
  emptyStateDescription?: string;
  emptyStateHint?: string;
  loadingMessage?: string;
  inputPlaceholder?: string;
  disabledPlaceholder?: string;
}

function renderInlineMarkdown(text: string): ReactNode[] {
  const nodes: ReactNode[] = [];
  const pattern = /(\[([^\]]+)\]\((https?:\/\/[^\s)]+)\)|`([^`]+)`|\*\*([^*]+)\*\*|\*([^*]+)\*)/g;
  let lastIndex = 0;
  let match: RegExpExecArray | null;

  while ((match = pattern.exec(text)) !== null) {
    if (match.index > lastIndex) {
      nodes.push(text.slice(lastIndex, match.index));
    }

    if (match[2] && match[3]) {
      nodes.push(
        <a
          key={`link-${match.index}`}
          href={match[3]}
          target="_blank"
          rel="noreferrer"
          className="text-blue-700 underline underline-offset-2"
        >
          {match[2]}
        </a>
      );
    } else if (match[4]) {
      nodes.push(
        <code
          key={`code-${match.index}`}
          className="rounded bg-gray-100 px-1 py-0.5 font-mono text-[12px] text-gray-800"
        >
          {match[4]}
        </code>
      );
    } else if (match[5]) {
      nodes.push(<strong key={`bold-${match.index}`}>{match[5]}</strong>);
    } else if (match[6]) {
      nodes.push(<em key={`italic-${match.index}`}>{match[6]}</em>);
    }

    lastIndex = pattern.lastIndex;
  }

  if (lastIndex < text.length) {
    nodes.push(text.slice(lastIndex));
  }

  return nodes;
}

function renderMarkdown(content: string): ReactNode {
  const lines = content.replace(/\r\n/g, '\n').split('\n');
  const blocks: ReactNode[] = [];
  let index = 0;

  while (index < lines.length) {
    const line = lines[index];

    if (!line.trim()) {
      index += 1;
      continue;
    }

    if (line.startsWith('```')) {
      const codeLines: string[] = [];
      index += 1;
      while (index < lines.length && !lines[index].startsWith('```')) {
        codeLines.push(lines[index]);
        index += 1;
      }
      if (index < lines.length && lines[index].startsWith('```')) {
        index += 1;
      }
      blocks.push(
        <pre
          key={`code-${index}`}
          className="my-1 overflow-x-auto rounded-md bg-gray-100 px-2 py-1.5 text-[12px] text-gray-800"
        >
          <code>{codeLines.join('\n')}</code>
        </pre>
      );
      continue;
    }

    const headingMatch = line.match(/^(#{1,3})\s+(.*)$/);
    if (headingMatch) {
      const level = headingMatch[1].length;
      const className = level === 1
        ? 'text-sm font-semibold text-gray-900'
        : level === 2
        ? 'text-[13px] font-semibold text-gray-900'
        : 'text-[12px] font-semibold text-gray-800';
      blocks.push(
        <div key={`heading-${index}`} className={`${className} mt-1`}>
          {renderInlineMarkdown(headingMatch[2])}
        </div>
      );
      index += 1;
      continue;
    }

    if (/^(-|\*)\s+/.test(line)) {
      const items: ReactNode[] = [];
      while (index < lines.length && /^(-|\*)\s+/.test(lines[index])) {
        items.push(
          <li key={`bullet-${index}`} className="leading-5">
            {renderInlineMarkdown(lines[index].replace(/^(-|\*)\s+/, ''))}
          </li>
        );
        index += 1;
      }
      blocks.push(
        <ul key={`list-${index}`} className="my-1 list-disc pl-5 space-y-0.5">
          {items}
        </ul>
      );
      continue;
    }

    if (/^\d+\.\s+/.test(line)) {
      const items: ReactNode[] = [];
      while (index < lines.length && /^\d+\.\s+/.test(lines[index])) {
        items.push(
          <li key={`ordered-${index}`} className="leading-5">
            {renderInlineMarkdown(lines[index].replace(/^\d+\.\s+/, ''))}
          </li>
        );
        index += 1;
      }
      blocks.push(
        <ol key={`ordered-list-${index}`} className="my-1 list-decimal pl-5 space-y-0.5">
          {items}
        </ol>
      );
      continue;
    }

    const paragraphLines: string[] = [];
    while (
      index < lines.length
      && lines[index].trim()
      && !lines[index].startsWith('```')
      && !/^(#{1,3})\s+/.test(lines[index])
      && !/^(-|\*)\s+/.test(lines[index])
      && !/^\d+\.\s+/.test(lines[index])
    ) {
      paragraphLines.push(lines[index]);
      index += 1;
    }

    blocks.push(
      <p key={`paragraph-${index}`} className="my-1 leading-5 text-gray-800">
        {renderInlineMarkdown(paragraphLines.join(' '))}
      </p>
    );
  }

  return <>{blocks}</>;
}

export function ChatPanel({
  messages,
  title,
  subtitle,
  conversationKey,
  onSubmit,
  loading = false,
  disabled = false,
  error = null,
  onDismissError,
  draftValue,
  onDraftChange,
  initialScrollTop = null,
  onScrollPositionChange,
  emptyStateEyebrow = 'Current chat',
  emptyStateDescription = 'Start a chat about the current document.',
  emptyStateHint = 'Replies are shown inline and the first conversation is saved automatically.',
  loadingMessage = 'Thinking through the document...',
  inputPlaceholder = 'Ask AI to help with your LaTeX...',
  disabledPlaceholder = 'Select an editable file to chat with AI',
}: ChatPanelProps) {
  const [internalInput, setInternalInput] = useState('');
  const [focused, setFocused] = useState(false);
  const messageListRef = useRef<HTMLDivElement>(null);
  const messageEndRef = useRef<HTMLDivElement>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const previousLastMessageIdRef = useRef<string | null>(messages[messages.length - 1]?.id || null);
  const previousLoadingRef = useRef(loading);
  const input = draftValue ?? internalInput;
  const hasMessages = messages.length > 0;
  const lastMessageId = useMemo(
    () => messages[messages.length - 1]?.id || null,
    [messages]
  );

  const setInput = (nextValue: string) => {
    if (draftValue !== undefined) {
      onDraftChange?.(nextValue);
      return;
    }

    setInternalInput(nextValue);
    onDraftChange?.(nextValue);
  };

  const resizeTextarea = () => {
    const textarea = textareaRef.current;
    if (!textarea) return;

    textarea.style.height = '0px';
    textarea.style.height = `${Math.min(textarea.scrollHeight, 160)}px`;
  };

  const scrollToBottom = () => {
    messageEndRef.current?.scrollIntoView({ behavior: 'smooth', block: 'end' });
  };

  useEffect(() => {
    resizeTextarea();
  }, [input]);

  useEffect(() => {
    const container = messageListRef.current;
    if (!container) return;

    const restoreScroll = window.requestAnimationFrame(() => {
      if (typeof initialScrollTop === 'number') {
        container.scrollTop = initialScrollTop;
        return;
      }

      if (hasMessages) {
        container.scrollTop = container.scrollHeight;
      }
    });

    return () => window.cancelAnimationFrame(restoreScroll);
  }, [conversationKey, hasMessages, initialScrollTop]);

  useEffect(() => {
    const hasNewMessage = lastMessageId !== previousLastMessageIdRef.current;
    const justStartedLoading = loading && !previousLoadingRef.current;

    if (hasNewMessage || justStartedLoading) {
      scrollToBottom();
    }

    previousLastMessageIdRef.current = lastMessageId;
    previousLoadingRef.current = loading;
  }, [lastMessageId, loading]);

  const submitPrompt = async () => {
    if (!input.trim() || loading || disabled) return;

    const nextPrompt = input.trim();
    setInput('');
    await onSubmit(nextPrompt);
  };

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    await submitPrompt();
  };

  const handleKeyDown = (event: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (event.key === 'Enter' && !event.shiftKey) {
      event.preventDefault();
      void submitPrompt();
    }
  };

  return (
    <div className="flex h-full flex-col bg-white">
      <div className="border-b border-editor-border bg-gray-50 px-4 py-3">
        <h2 className="text-sm font-semibold text-gray-900">{title || 'Current Chat'}</h2>
        <p className="mt-0.5 text-xs text-gray-500">
          {subtitle || 'Ask AI for document edits, explanations, or follow-up revisions.'}
        </p>
      </div>

      <div className="flex-1 min-h-0 px-4 py-3">
        <div
          ref={messageListRef}
          className="h-full overflow-y-auto"
          onScroll={(event) => onScrollPositionChange?.(event.currentTarget.scrollTop)}
        >
          {messages.length === 0 ? (
            <div className="px-2 py-4">
              <div className="mb-1 flex items-center gap-2 text-[11px] font-medium uppercase tracking-wide text-gray-400">
                <MessageSquare className="h-3.5 w-3.5" />
                {emptyStateEyebrow}
              </div>
              <p className="text-sm text-gray-600">{emptyStateDescription}</p>
              <p className="mt-1 text-xs text-gray-400">
                {emptyStateHint}
              </p>
            </div>
          ) : (
            <div className="space-y-3">
              {messages.map((message) => (
                <div key={message.id} className="border-b border-gray-100 pb-3 last:border-b-0 last:pb-0">
                  <div className="mb-1 text-[11px] font-medium uppercase tracking-wide text-gray-400">
                    {message.role === 'user' ? 'You' : 'AI'}
                  </div>
                  <div className="text-sm text-gray-800">
                    {renderMarkdown(message.content)}
                  </div>
                </div>
              ))}
            </div>
          )}

          {loading && (
            <div className="mt-3 flex items-center gap-2 text-sm text-gray-500">
              <Loader2 className="h-4 w-4 animate-spin" />
              {loadingMessage}
            </div>
          )}

          <div ref={messageEndRef} />
        </div>
      </div>

      <div className="border-t border-editor-border bg-white p-4">
        {error && (
          <div className="mb-3 flex items-center gap-2 rounded-xl border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
            <AlertCircle className="h-4 w-4 flex-shrink-0" />
            <span className="flex-1">{error}</span>
            {onDismissError && (
              <button
                type="button"
                aria-label="Dismiss chat error"
                onClick={onDismissError}
                className="rounded p-1 transition-colors hover:bg-red-100"
              >
                <X className="h-3 w-3" />
              </button>
            )}
          </div>
        )}

        <form
          onSubmit={handleSubmit}
          className={`
            flex items-center gap-2 rounded-xl border border-gray-200 bg-white px-3 py-2
            shadow-sm transition-all duration-200
            ${focused ? 'border-transparent ring-2 ring-blue-500' : 'hover:border-gray-300'}
          `}
        >
          <Sparkles className="h-4 w-4 flex-shrink-0 text-blue-500" />
          <textarea
            ref={textareaRef}
            value={input}
            onChange={(event) => setInput(event.target.value)}
            onFocus={() => setFocused(true)}
            onBlur={() => setFocused(false)}
            onKeyDown={handleKeyDown}
            rows={1}
            placeholder={disabled
              ? disabledPlaceholder
              : loading
              ? 'Waiting for a response...'
              : inputPlaceholder}
            disabled={loading || disabled}
            className="max-h-40 min-h-[40px] flex-1 resize-none border-none bg-transparent py-1 text-sm outline-none placeholder-gray-400 disabled:text-gray-400"
          />
          <button
            type="submit"
            aria-label="Send chat prompt"
            disabled={!input.trim() || loading || disabled}
            className="flex-shrink-0 rounded-lg bg-blue-500 p-1.5 text-white transition-colors hover:bg-blue-600 disabled:cursor-not-allowed disabled:opacity-40"
          >
            {loading ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <Send className="h-4 w-4" />
            )}
          </button>
        </form>
      </div>
    </div>
  );
}
